import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import api from '../api';
import { getTagColor } from '../utils';

function EventDetails({ user }) {
  const { id } = useParams();
  const history = useHistory();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eligibleHelpers, setEligibleHelpers] = useState({});  // Map of shiftId -> eligible users
  const [selectedUser, setSelectedUser] = useState({});
  const [overlaps, setOverlaps] = useState({});
  const [showAllShifts, setShowAllShifts] = useState(false);

  // Check if user can assign helpers (admin or team admin)
  const canAssignHelpers = user?.is_admin || user?.is_team_admin;

  // Get user's helferpool tags
  const userTags = user?.helferpool
    ? user.helferpool.split(/;\s*/).map(t => t.trim()).filter(t => t)
    : [];

  // Check if user has relevant helferpool tags (excluding "Helferpool befreit", and not admin)
  const userHasRelevantTags = !user?.is_admin &&
    userTags.filter(t => t.toLowerCase() !== 'helferpool befreit').length > 0;

  useEffect(() => {
    loadEvent();
    if (user) {
      loadOverlaps();
    }
  }, [id, user]);

  // Load eligible helpers when event is loaded (for admins and team admins)
  useEffect(() => {
    if (event && canAssignHelpers) {
      loadEligibleHelpersForShifts(event.shifts || []);
    }
  }, [event, canAssignHelpers]);

  const loadEvent = async () => {
    try {
      const res = await api.get(`/events/${id}`);
      setEvent(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEligibleHelpersForShifts = async (shifts) => {
    const helpersMap = {};
    for (const shift of shifts) {
      try {
        const res = await api.get(`/shifts/${shift.id}/eligible-helpers`);
        helpersMap[shift.id] = res.data;
      } catch (error) {
        console.error(`Error loading eligible helpers for shift ${shift.id}:`, error);
        helpersMap[shift.id] = [];
      }
    }
    setEligibleHelpers(helpersMap);
  };

  const loadOverlaps = async () => {
    try {
      const res = await api.get('/shifts/check-overlaps');
      setOverlaps(res.data);
    } catch (error) {
      console.error('Error loading overlaps:', error);
    }
  };

  // Helper function to check if a shift should be visible to the user
  const isShiftVisibleToUser = (shift) => {
    // Admins see all shifts
    if (user?.is_admin) {
      return true;
    }

    // If "show all" is enabled, show everything
    if (showAllShifts) {
      return true;
    }

    // Get user's helferpool tags (excluding "Helferpool befreit")
    const relevantUserTags = userTags
      .map(t => t.toLowerCase())
      .filter(t => t && t !== 'helferpool befreit');

    // If user has no relevant tags, show all shifts
    if (relevantUserTags.length === 0) {
      return true;
    }

    // Get shift's tags
    const shiftTags = shift.tags
      ? shift.tags.split(/;\s*/).map(t => t.trim().toLowerCase()).filter(t => t)
      : [];

    // If shift has no tags, show to everyone
    if (shiftTags.length === 0) {
      return true;
    }

    // Check if any user tag matches any shift tag
    return relevantUserTags.some(userTag => shiftTags.includes(userTag));
  };

  const handleSignUp = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/signup`, {});
      loadEvent();
      loadOverlaps();
    } catch (error) {
      const message = error.response?.data?.message || 'Fehler beim Anmelden';
      alert(message);
    }
  };

  const handleSignOut = async (shiftId) => {
    try {
      await api.post(`/shifts/${shiftId}/signout`, {});
      loadEvent();
      loadOverlaps();
    } catch (error) {
      alert(error.response?.data?.message || 'Fehler beim Abmelden');
    }
  };

  const handleAdminSignUp = async (shiftId, userId) => {
    if (!userId) return;
    try {
      await api.post(`/shifts/${shiftId}/signup`, { user_id: userId });
      setSelectedUser({ ...selectedUser, [shiftId]: '' });
      loadEvent();
    } catch (error) {
      alert(error.response?.data?.message || 'Fehler beim Eintragen');
    }
  };

  const handleAdminSignOut = async (shiftId, userId) => {
    try {
      await api.post(`/shifts/${shiftId}/signout`, { user_id: userId });
      loadEvent();
    } catch (error) {
      alert(error.response?.data?.message || 'Fehler beim Austragen');
    }
  };

  const isUserSignedUpForShift = (shift) => {
    return user && shift.helpers && shift.helpers.some(h => h.email === user.email);
  };

  const isShiftFull = (shift) => {
    return shift.helpers && shift.helpers.length >= shift.helpers_needed;
  };

  const getAvailableUsers = (shift) => {
    // Use eligible helpers from server (already filtered and excludes signed-up users)
    return eligibleHelpers[shift.id] || [];
  };


  // Format user display name with season shifts count and helferpool tags
  const formatUserOption = (u) => {
    const name = u.first_name || u.last_name
      ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
      : u.email;

    const count = u.season_shifts_count !== undefined ? ` [${u.season_shifts_count}]` : '';
    const pool = u.helferpool ? ` (${u.helferpool})` : '';

    return `${name}${count}${pool}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) return <div>Lädt...</div>;
  if (!event) return <div>Event nicht gefunden</div>;

  return (
    <div>
      <button
        onClick={() => history.push('/')}
        style={{ marginBottom: '1rem', background: 'none', border: 'none', color: '#C62828', cursor: 'pointer', fontSize: '1rem' }}
      >
        ← Zurück
      </button>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <div>
            <h1>{event.title}</h1>
            <p style={{ color: '#666', fontSize: '1.1rem', margin: '1rem 0 0.5rem 0' }}>
              🗓️ {formatDate(event.date)}
            </p>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>
              📍 {event.location}
            </p>
          </div>
          {(() => {
            const visibleShifts = event.shifts?.filter(isShiftVisibleToUser) || [];
            return (
              <div style={{
                padding: '1rem 1.5rem',
                backgroundColor: '#e3f2fd',
                borderRadius: '8px',
                textAlign: 'center',
                height: 'fit-content'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                  {visibleShifts.reduce((sum, s) => sum + (s.helpers?.length || 0), 0)}/
                  {visibleShifts.reduce((sum, s) => sum + s.helpers_needed, 0)}
                </div>
                <div>Helfer gesamt</div>
              </div>
            );
          })()}
        </div>

        {event.games && event.games.length > 0 && (
          <div style={{ marginBottom: '2rem' }}>
            <h3>Spiele</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {event.games.map((game) => (
                <div key={game.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}>
                  <span style={{ fontWeight: 'bold', minWidth: '50px' }}>{game.game_time}</span>
                  {game.league_short && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      backgroundColor: '#e3f2fd',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      minWidth: '60px',
                      textAlign: 'center'
                    }}>
                      {game.league_short}
                    </span>
                  )}
                  <span style={{ flex: 1 }}>
                    {game.team_a} <span style={{ color: '#888' }}>vs</span> {game.team_b}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {event.description && (
          <div style={{ marginBottom: '2rem' }}>
            <h3>Beschreibung</h3>
            <p style={{ color: '#555', lineHeight: '1.6', whiteSpace: 'pre-line' }}>{event.description}</p>
          </div>
        )}

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: 0 }}>Helfereinsätze</h3>
            {userHasRelevantTags && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={showAllShifts}
                  onChange={(e) => setShowAllShifts(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                Alle Einsätze anzeigen
              </label>
            )}
          </div>
          {(() => {
            const visibleShifts = event.shifts?.filter(isShiftVisibleToUser) || [];
            return visibleShifts.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {visibleShifts.map((shift) => {
                const isSignedUp = isUserSignedUpForShift(shift);
                const isFull = isShiftFull(shift);
                const availableUsers = getAvailableUsers(shift);

                return (
                  <div key={shift.id} className="card" style={{ backgroundColor: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 0.5rem 0' }}>{shift.name}</h4>
                        <p style={{ color: '#666', margin: '0.25rem 0' }}>
                          🕐 {shift.start_time} - {shift.end_time} Uhr ({shift.duration_minutes} Min.)
                        </p>
                        {shift.tags && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.25rem' }}>
                            {shift.tags.split(/;\s*/).map((tag, idx) => {
                              const trimmedTag = tag.trim();
                              if (!trimmedTag) return null;
                              const color = getTagColor(trimmedTag);
                              return (
                                <span key={idx} style={{
                                  display: 'inline-block',
                                  padding: '0.15rem 0.5rem',
                                  borderRadius: '12px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500',
                                  backgroundColor: color.bg,
                                  color: color.text,
                                  whiteSpace: 'nowrap'
                                }}>
                                  {trimmedTag}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      <div style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: isFull ? '#e8f5e9' : '#fff3e0',
                        borderRadius: '4px',
                        textAlign: 'center',
                        marginLeft: '1rem'
                      }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                          {shift.helpers?.length || 0}/{shift.helpers_needed}
                        </div>
                        <div style={{ fontSize: '0.8rem' }}>Helfer</div>
                      </div>
                    </div>

                    {user && (
                      <div style={{ marginBottom: '1rem' }}>
                        {isSignedUp ? (
                          <button
                            onClick={() => handleSignOut(shift.id)}
                            className="btn btn-secondary"
                          >
                            Abmelden
                          </button>
                        ) : (
                          <>
                            {overlaps[shift.id] && (
                              <div style={{
                                padding: '0.75rem',
                                backgroundColor: '#fff3e0',
                                border: '1px solid #ffcc80',
                                borderRadius: '4px',
                                marginBottom: '0.75rem',
                                fontSize: '0.9rem',
                                color: '#e65100'
                              }}>
                                <strong>⚠️ Überlappung:</strong> Du bist bereits eingetragen für
                                <br />
                                <span style={{ fontWeight: 'bold' }}>
                                  {overlaps[shift.id].overlapping_event} - {overlaps[shift.id].overlapping_shift}
                                </span>
                                <br />
                                <span style={{ fontSize: '0.85rem' }}>
                                  ({new Date(overlaps[shift.id].overlapping_date).toLocaleDateString('de-DE')}, {overlaps[shift.id].overlapping_time} Uhr)
                                </span>
                              </div>
                            )}
                            <button
                              onClick={() => handleSignUp(shift.id)}
                              className="btn btn-success"
                              disabled={isFull || overlaps[shift.id]}
                              style={overlaps[shift.id] ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                            >
                              {isFull ? 'Einsatz ist voll' : overlaps[shift.id] ? 'Überlappung mit anderem Einsatz' : 'Für diesen Einsatz anmelden'}
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    {/* Admin/Team Admin: Add other users */}
                    {canAssignHelpers && !isFull && availableUsers.length > 0 && (
                      <div style={{
                        marginBottom: '1rem',
                        padding: '0.75rem',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px'
                      }}>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
                          Helfer eintragen {user?.is_team_admin && !user?.is_admin ? '(Team Admin)' : '(Admin)'}:
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <select
                            value={selectedUser[shift.id] || ''}
                            onChange={(e) => setSelectedUser({ ...selectedUser, [shift.id]: e.target.value })}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #ddd'
                            }}
                          >
                            <option value="">Benutzer auswählen...</option>
                            {availableUsers.map(u => (
                              <option key={u.id} value={u.id}>
                                {formatUserOption(u)}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() => handleAdminSignUp(shift.id, parseInt(selectedUser[shift.id]))}
                            className="btn"
                            disabled={!selectedUser[shift.id]}
                            style={{ padding: '0.5rem 1rem' }}
                          >
                            Eintragen
                          </button>
                        </div>
                      </div>
                    )}

                    <div>
                      <strong style={{ fontSize: '0.9rem', color: '#666' }}>
                        Angemeldete Helfer ({shift.helpers?.length || 0}):
                      </strong>
                      {shift.helpers && shift.helpers.length > 0 ? (
                        <ul style={{ listStyle: 'none', marginTop: '0.5rem', paddingLeft: 0 }}>
                          {shift.helpers.map((helper, idx) => (
                            <li key={idx} style={{
                              padding: '0.5rem',
                              backgroundColor: '#fff',
                              marginBottom: '0.25rem',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>{helper.email}</span>
                              {canAssignHelpers && helper.email !== user.email && (
                                <button
                                  onClick={() => handleAdminSignOut(shift.id, helper.id)}
                                  className="btn btn-danger"
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                                >
                                  Austragen
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p style={{ color: '#999', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                          Noch keine Helfer angemeldet
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{ color: '#666' }}>Keine Einsätze für dich verfügbar</p>
          );
          })()}
        </div>
      </div>
    </div>
  );
}

export default EventDetails;
